import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

/**
 * BiometricPulseEntry
 * ───────────────────
 * A full-screen "Identity Scan" overlay that sweeps a tier-tinted line
 * across the viewport for ~1.4s before revealing the wallet. Shown
 * only once per session — subsequent visits skip the sweep.
 *
 * Persists a `vinci_wallet_scanned` flag in sessionStorage so browsing
 * away and back inside a tab does not re-scan every time.
 */
export function BiometricPulseEntry({ tier, sessionKey = "vinci_wallet_scanned", onDone }) {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return !sessionStorage.getItem(sessionKey); } catch { return true; }
  });

  useEffect(() => {
    if (!visible) { onDone && onDone(); return; }
    const mq = typeof window !== "undefined"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => {
      setVisible(false);
      try { sessionStorage.setItem(sessionKey, "1"); } catch { /* noop */ }
      onDone && onDone();
    }, mq ? 300 : 1400);
    return () => clearTimeout(t);
  }, [visible, sessionKey, onDone]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="biometric-pulse"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35 } }}
          className="fixed inset-0 z-[80] pointer-events-none"
          style={{
            background:
              "radial-gradient(1400px 900px at 50% 50%, rgba(5,5,5,0.65), rgba(5,5,5,0.98))",
          }}
        >
          {/* scanning line */}
          <motion.div
            className="absolute left-0 right-0 h-[2px]"
            style={{
              background:
                `linear-gradient(90deg, transparent, ${tier.accent}, ${tier.primary}, ${tier.accent}, transparent)`,
              boxShadow: `0 0 30px ${tier.glow}, 0 0 12px ${tier.glow}`,
            }}
            initial={{ top: "0%" }}
            animate={{ top: ["0%", "100%", "0%"] }}
            transition={{ duration: 1.3, ease: [0.65, 0, 0.35, 1] }}
          />

          {/* corner brackets */}
          <div className="absolute inset-8 md:inset-16 pointer-events-none">
            {[
              "top-0 left-0 border-l border-t",
              "top-0 right-0 border-r border-t",
              "bottom-0 left-0 border-l border-b",
              "bottom-0 right-0 border-r border-b",
            ].map((pos, i) => (
              <motion.div
                key={i}
                className={`absolute w-12 h-12 ${pos}`}
                style={{ borderColor: tier.primary }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: [0, 1, 1, 0.4], scale: [0.6, 1, 1, 1] }}
                transition={{ duration: 1.2, delay: 0.05 * i }}
              />
            ))}
          </div>

          {/* identity label */}
          <motion.div
            className="absolute inset-0 grid place-items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.3, times: [0, 0.25, 0.75, 1] }}
          >
            <div className="text-center">
              <ShieldCheck
                size={28}
                style={{ color: tier.primary, filter: `drop-shadow(0 0 14px ${tier.glow})` }}
                className="mx-auto"
              />
              <div
                className="font-grotesk text-[10px] uppercase tracking-[0.6em] mt-4"
                style={{ color: tier.accent }}
              >
                Identity Verified
              </div>
              <div
                className="font-serif italic text-lg mt-1"
                style={{ color: "#F5F5F0" }}
              >
                Vinci Executive Vault
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
